/**
 * React hook for saved track filters on the Track Translations page, backed by
 * the users DAO via the UserPreference GraphQL API.
 *
 * This replaces the localStorage-based `savedTrackSearchUtils.ts` functions with
 * a hook that fetches/writes through the backend.
 */

import { DateRangePickerProps } from '@amzn/awsui-components-console/date-range-picker';
import { UserPreferenceKey } from 'src/generated/graphql';
import { useUserPreferences } from 'src/common/savedSearches/useUserPreferences';
import { SavedSearchEntry } from 'src/common/savedSearches';
import { RELATIVE_RANGE_MESSAGE_IDS } from 'src/common/utils/dateFormatters';
import type { TranslateFn } from 'src/i18n/shim/useTranslation';

/** The filter selection a saved search restores. */
export interface SavedTrackSearchState {
  /** Namespace ID. Always present — the page cannot query without one. */
  namespace: string;
  /** String group ID, or '' meaning all groups. */
  stringGroup: string;
  /** Date range, or null when the user cleared it. */
  dateRange: DateRangePickerProps.Value | null;
}

export type SavedTrackSearch = SavedSearchEntry<SavedTrackSearchState>;

/** Maximum saved track searches kept. */
export const MAX_SAVED_TRACK_SEARCHES = 5;

function isStoredDateRangeValid(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value !== 'object') return false;

  const range = value as Record<string, unknown>;
  if (range.type === 'relative') {
    return typeof range.amount === 'number' && typeof range.unit === 'string';
  }
  if (range.type === 'absolute') {
    return (
      typeof range.startDate === 'string' && typeof range.endDate === 'string'
    );
  }
  return false;
}

function parseState(raw: unknown): SavedTrackSearchState | null {
  if (!raw || typeof raw !== 'object') return null;

  const state = raw as Record<string, unknown>;

  if (typeof state.namespace !== 'string' || state.namespace.length === 0) {
    return null;
  }
  if (typeof state.stringGroup !== 'string') return null;
  if (!isStoredDateRangeValid(state.dateRange)) return null;

  return {
    namespace: state.namespace,
    stringGroup: state.stringGroup,
    dateRange:
      (state.dateRange as DateRangePickerProps.Value | null | undefined) ??
      null,
  };
}

/**
 * The name suggested for a saved filter selection. Ids and ISO dates are data, interpolated
 * verbatim; the relative range resolves the shared `RELATIVE_RANGE_MESSAGE_IDS` so this label and
 * the picker above it word it identically. Only ever a SUGGESTION — once saved the label is stored
 * verbatim and never re-derived, so an entry keeps the wording it was created with.
 */
export function generateTrackSearchLabel(
  t: TranslateFn,
  state: SavedTrackSearchState,
): string {
  const parts: string[] = [state.namespace];
  parts.push(state.stringGroup || t('track-saved-label-all-groups'));

  if (state.dateRange) {
    if (state.dateRange.type === 'relative') {
      parts.push(
        t(RELATIVE_RANGE_MESSAGE_IDS[state.dateRange.unit], {
          amount: state.dateRange.amount,
        }),
      );
    } else {
      parts.push(
        t('track-saved-label-date-range', {
          startDate: state.dateRange.startDate,
          endDate: state.dateRange.endDate,
        }),
      );
    }
  }

  return parts.join(' · ').slice(0, 50);
}

/**
 * Hook providing saved track filters for the Track page. Fetches from and
 * persists to the backend via GraphQL.
 */
export function useSavedTrackSearches() {
  return useUserPreferences<SavedTrackSearchState>({
    preferenceKey: UserPreferenceKey.SavedTrackFilters,
    parseState,
    generateLabel: generateTrackSearchLabel,
    maxEntries: MAX_SAVED_TRACK_SEARCHES,
  });
}
